<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="title"
    :subtitle="subtitle"
    :loading="loading"
    :save-disabled="saveDisabled"
    :extra-action-label="extraActionLabel"
    :extra-action-color="extraActionColor"
    :extra-action-disabled="extraActionDisabled"
    :max-width="maxWidth"
    @update:model-value="emitModel"
    @extra-action="$emit('extra-action')"
    @save="handleSave"
  >
    <v-row>
      <v-col cols="12" md="7">
        <v-text-field
          v-model="form.name"
          :label="$t('ncrm.leads.wizard.fields.boardName')"
          :error-messages="nameErrors"
          required
          @update:model-value="markDirty"
        />
      </v-col>
      <v-col cols="12" md="5">
        <v-select
          v-model="form.entity_scope"
          :label="$t('ncrm.leads.wizard.fields.entityScope')"
          :items="entityScopeOptions"
          item-title="label"
          item-value="value"
          :menu-props="selectMenuProps"
          hide-details
          @update:model-value="markDirty"
        />
      </v-col>

      <v-col v-if="showAutoConvertSettings" cols="12">
        <v-card variant="outlined" class="pa-3">
          <div class="text-subtitle-1 ncrm-text-strong mb-3">
            {{ $t("ncrm.leads.wizard.autoConvert.title") }}
          </div>
          <v-switch
            v-model="form.auto_convert_on_qualified"
            color="primary"
            :label="$t('ncrm.leads.wizard.autoConvert.enabled')"
            hide-details
            inset
            @update:model-value="markDirty"
          />
          <v-row class="mt-1">
            <v-col cols="12" md="6">
              <v-select
                v-model="form.auto_convert_deal_pipeline_id"
                :label="$t('ncrm.leads.wizard.autoConvert.dealPipeline')"
                :items="dealPipelineOptions"
                item-title="label"
                item-value="id"
                :menu-props="selectMenuProps"
                :disabled="!form.auto_convert_on_qualified || !dealPipelineOptions.length"
                hide-details
                @update:model-value="onAutoConvertPipelineChanged"
              />
            </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.auto_convert_deal_stage_id"
                  :label="$t('ncrm.leads.wizard.autoConvert.dealStage')"
                :items="autoConvertDealStageOptions"
                item-title="label"
                item-value="id"
                :menu-props="selectMenuProps"
                :disabled="!form.auto_convert_on_qualified || !autoConvertDealStageOptions.length"
                hide-details
                  @update:model-value="markDirty"
                />
              </v-col>
            </v-row>
            <div class="text-caption text-medium-emphasis mt-2">
              {{ $t("ncrm.leads.wizard.autoConvert.destinationHelp") }}
            </div>
          </v-card>
        </v-col>

      <v-col cols="12">
        <v-card variant="outlined" class="pa-3">
          <div class="text-subtitle-1 ncrm-text-strong mb-3">
            {{ $t("ncrm.leads.wizard.autoMissingTask.title") }}
          </div>
          <v-switch
            v-model="form.auto_create_task_if_missing_after_change"
            color="primary"
            :label="$t('ncrm.leads.wizard.autoMissingTask.enabled')"
            hide-details
            inset
            @update:model-value="markDirty"
          />
          <v-row class="mt-1">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="form.auto_create_task_if_missing_delay_minutes"
                :label="$t('ncrm.leads.wizard.autoMissingTask.delayMinutes')"
                type="number"
                min="1"
                max="10080"
                step="1"
                hide-details
                :disabled="!form.auto_create_task_if_missing_after_change"
                @update:model-value="onAutoMissingTaskDelayInput"
              />
            </v-col>
            <v-col cols="12" md="7" class="d-flex align-center">
              <div class="text-caption text-medium-emphasis">
                {{ $t("ncrm.leads.wizard.autoMissingTask.help") }}
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" class="pt-1">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
          <div class="text-subtitle-1 ncrm-text-strong">
            {{ $t("ncrm.leads.wizard.stageEditor.title") }}
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              prepend-icon="mdi-restore"
              :disabled="loading"
              @click="restoreDefaultStages"
            >
              {{ $t("ncrm.leads.wizard.stageEditor.restoreDefaults") }}
            </v-btn>
            <v-btn
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-plus"
              :disabled="loading"
              @click="addStage"
            >
              {{ $t("ncrm.leads.wizard.stageEditor.addStage") }}
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="stageErrors.length"
          type="warning"
          density="comfortable"
          class="mb-3"
        >
          <div v-for="(error, index) in stageErrors" :key="`stage-error-${index}`">
            {{ error }}
          </div>
        </v-alert>

        <div class="ncrm-lead-wizard-stages">
          <div
            v-for="(stage, index) in localStages"
            :key="stage._uid"
            class="ncrm-lead-wizard-stage-row"
          >
            <div class="ncrm-lead-wizard-stage-row__index text-caption text-medium-emphasis">
              {{ index + 1 }}
            </div>
            <v-text-field
              v-model="stage.name"
              :label="$t('ncrm.leads.wizard.stageEditor.stageName')"
              hide-details
              class="ncrm-lead-wizard-stage-row__name"
              @update:model-value="(value) => handleStageNameInput(index, value)"
            />
            <v-text-field
              v-model="stage.stage_key"
              :label="$t('ncrm.leads.wizard.stageEditor.stageKey')"
              hide-details
              class="ncrm-lead-wizard-stage-row__key"
              @update:model-value="() => handleStageKeyInput(index)"
            />
            <v-select
              v-model="stage.stage_type"
              :label="$t('ncrm.leads.wizard.stageEditor.stageType')"
              :items="stageTypeOptions"
              item-title="label"
              item-value="value"
              :menu-props="selectMenuProps"
              hide-details
              class="ncrm-lead-wizard-stage-row__type"
              @update:model-value="markDirty"
            />
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                  :disabled="loading"
                  @click="setDefaultStage(index)"
                >
                  <v-icon>
                    {{ stage.is_default ? "mdi-star" : "mdi-star-outline" }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ $t("ncrm.leads.wizard.stageEditor.markDefault") }}</span>
            </v-tooltip>
            <v-btn
              icon="mdi-arrow-up"
              size="small"
              variant="text"
              color="primary"
              :disabled="loading || index === 0"
              @click="moveStage(index, -1)"
            />
            <v-btn
              icon="mdi-arrow-down"
              size="small"
              variant="text"
              color="primary"
              :disabled="loading || index >= localStages.length - 1"
              @click="moveStage(index, 1)"
            />
            <v-btn
              icon="mdi-delete-outline"
              size="small"
              variant="text"
              color="error"
              :disabled="loading || localStages.length <= 1"
              @click="removeStage(index)"
            />
          </div>
        </div>
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
</template>

<script>
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  cloneNcrmLeadDefaultStages,
  inferNcrmLeadStageTypeFromKey,
  normalizeNcrmLeadEntityScope,
  normalizeNcrmLeadStageKey,
  normalizeNcrmLeadStageType,
  normalizeNcrmLeadString,
} from "@/Utile/ncrmLeads.mjs";

const toUid = () => `ncrm-stage-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;
const AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES = 60;
const AUTO_MISSING_TASK_DELAY_MIN_MINUTES = 1;
const AUTO_MISSING_TASK_DELAY_MAX_MINUTES = 10080;
const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const normalizeAutoMissingTaskDelayMinutes = (value, fallback = AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const normalized = Math.trunc(parsed);
  if (normalized < AUTO_MISSING_TASK_DELAY_MIN_MINUTES) return AUTO_MISSING_TASK_DELAY_MIN_MINUTES;
  if (normalized > AUTO_MISSING_TASK_DELAY_MAX_MINUTES) return AUTO_MISSING_TASK_DELAY_MAX_MINUTES;
  return normalized;
};

const normalizeStageRow = (row = {}, index = 0) => {
  const name = normalizeNcrmLeadString(row.name || row.label).slice(0, 120);
  const stageKey = normalizeNcrmLeadStageKey(row.stage_key ?? row.stageKey ?? name);
  const stageType = normalizeNcrmLeadStageType(
    row.stage_type ?? row.stageType,
    inferNcrmLeadStageTypeFromKey(stageKey)
  );
  return {
    id: Number(row.id || 0) || null,
    name,
    stage_key: stageKey,
    stage_type: stageType || "open",
    is_default: Boolean(row.is_default ?? row.isDefault ?? index === 0),
    _uid: toUid(),
    _manual_stage_key: Boolean(normalizeNcrmLeadString(row.stage_key ?? row.stageKey)),
  };
};

export default {
  name: "NcrmLeadBoardWizard",
  components: {
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    board: {
      type: Object,
      default: null,
    },
    showAutoConvertSettings: {
      type: Boolean,
      default: false,
    },
    dealPipelines: {
      type: Array,
      default: () => [],
    },
    stages: {
      type: Array,
      default: () => [],
    },
    maxWidth: {
      type: [Number, String],
      default: 980,
    },
    extraActionLabel: {
      type: String,
      default: "",
    },
    extraActionColor: {
      type: String,
      default: "primary",
    },
    extraActionDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "submit", "extra-action"],
  data() {
    return {
      form: {
        name: "",
        entity_scope: "both",
        auto_convert_on_qualified: true,
        auto_convert_deal_pipeline_id: null,
        auto_convert_deal_stage_id: null,
        auto_create_task_if_missing_after_change: false,
        auto_create_task_if_missing_delay_minutes: AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES,
      },
      localStages: [],
      nameErrors: [],
      stageErrors: [],
      dirty: false,
    };
  },
  computed: {
    saveDisabled() {
      if (this.loading) return true;
      return !normalizeNcrmLeadString(this.form.name) || !this.localStages.length;
    },
    entityScopeOptions() {
      return [
        {
          value: "both",
          label: this.$t("ncrm.leads.entityScope.both"),
        },
        {
          value: "company_only",
          label: this.$t("ncrm.leads.entityScope.company_only"),
        },
        {
          value: "person_only",
          label: this.$t("ncrm.leads.entityScope.person_only"),
        },
      ];
    },
    stageTypeOptions() {
      return [
        {
          value: "open",
          label: this.$t("ncrm.leads.stageType.open"),
        },
        {
          value: "conversion",
          label: this.$t("ncrm.leads.stageType.conversion"),
        },
        {
          value: "closed",
          label: this.$t("ncrm.leads.stageType.closed"),
        },
      ];
    },
    dealPipelineOptions() {
      return (Array.isArray(this.dealPipelines) ? this.dealPipelines : [])
        .filter((pipeline) => Number(pipeline?.is_active) === 1 || pipeline?.is_active === true)
        .map((pipeline) => ({
          id: parsePositiveId(pipeline?.id),
          label: normalizeNcrmLeadString(pipeline?.name) || `#${parsePositiveId(pipeline?.id) || "-"}`,
        }))
        .filter((item) => item.id);
    },
    autoConvertDealStageOptions() {
      const pipelineId = parsePositiveId(this.form.auto_convert_deal_pipeline_id);
      if (!pipelineId) return [];
      const pipeline = (Array.isArray(this.dealPipelines) ? this.dealPipelines : []).find(
        (item) => parsePositiveId(item?.id) === pipelineId
      );
      if (!pipeline) return [];
      return (Array.isArray(pipeline?.stages) ? pipeline.stages : [])
        .filter((stage) => Number(stage?.is_active) === 1 || stage?.is_active === true)
        .map((stage) => ({
          id: parsePositiveId(stage?.id),
          label: normalizeNcrmLeadString(stage?.name) || `#${parsePositiveId(stage?.id) || "-"}`,
        }))
        .filter((item) => item.id);
    },
    selectMenuProps() {
      return {
        contentClass: "ncrm-lead-wizard-select-menu",
        zIndex: 2710,
      };
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (value) {
          this.resetState();
        }
      },
    },
    dealPipelines() {
      if (!this.modelValue || !this.showAutoConvertSettings) return;
      this.ensureAutoConvertSelection();
    },
  },
  methods: {
    emitModel(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    resetState() {
      this.nameErrors = [];
      this.stageErrors = [];
      this.dirty = false;

      const boardName = normalizeNcrmLeadString(this.board?.name).slice(0, 190);
      const boardScope = normalizeNcrmLeadEntityScope(this.board?.entity_scope ?? this.board?.entityScope, "both");
      this.form = {
        name: boardName,
        entity_scope: boardScope || "both",
        auto_convert_on_qualified:
          this.board?.auto_convert_on_qualified === false || Number(this.board?.auto_convert_on_qualified) === 0
            ? false
            : true,
        auto_convert_deal_pipeline_id: parsePositiveId(this.board?.auto_convert_deal_pipeline_id),
        auto_convert_deal_stage_id: parsePositiveId(this.board?.auto_convert_deal_stage_id),
        auto_create_task_if_missing_after_change:
          this.board?.auto_create_task_if_missing_after_change === true ||
          Number(this.board?.auto_create_task_if_missing_after_change) === 1,
        auto_create_task_if_missing_delay_minutes: normalizeAutoMissingTaskDelayMinutes(
          this.board?.auto_create_task_if_missing_delay_minutes,
          AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES
        ),
      };
      this.ensureAutoConvertSelection();

      const initialStages = Array.isArray(this.stages) && this.stages.length
        ? this.stages
        : cloneNcrmLeadDefaultStages();
      this.localStages = initialStages.map((stage, index) => normalizeStageRow(stage, index));
      this.ensureDefaultStage();
    },
    markDirty() {
      this.dirty = true;
      this.nameErrors = [];
      this.stageErrors = [];
    },
    addStage() {
      this.localStages.push(
        normalizeStageRow(
          {
            name: "",
            stage_key: "",
            stage_type: "open",
            is_default: false,
          },
          this.localStages.length
        )
      );
      this.markDirty();
    },
    removeStage(index) {
      if (this.localStages.length <= 1) return;
      this.localStages.splice(index, 1);
      this.ensureDefaultStage();
      this.markDirty();
    },
    moveStage(index, delta) {
      const targetIndex = index + delta;
      if (targetIndex < 0 || targetIndex >= this.localStages.length) return;
      const [item] = this.localStages.splice(index, 1);
      this.localStages.splice(targetIndex, 0, item);
      this.markDirty();
    },
    setDefaultStage(index) {
      this.localStages = this.localStages.map((stage, stageIndex) => ({
        ...stage,
        is_default: stageIndex === index,
      }));
      this.markDirty();
    },
    ensureDefaultStage() {
      if (!this.localStages.length) return;
      let hasDefault = false;
      this.localStages = this.localStages.map((stage, index) => {
        if (!hasDefault && stage.is_default) {
          hasDefault = true;
          return stage;
        }
        if (hasDefault) {
          return {
            ...stage,
            is_default: false,
          };
        }
        if (!hasDefault && index === 0) {
          hasDefault = true;
          return {
            ...stage,
            is_default: true,
          };
        }
        return stage;
      });
    },
    restoreDefaultStages() {
      this.localStages = cloneNcrmLeadDefaultStages().map((stage, index) => normalizeStageRow(stage, index));
      this.ensureDefaultStage();
      this.markDirty();
    },
    ensureAutoConvertSelection() {
      if (!this.dealPipelineOptions.length) return;
      const selectedPipelineId = parsePositiveId(this.form.auto_convert_deal_pipeline_id);
      if (!selectedPipelineId || !this.dealPipelineOptions.some((item) => item.id === selectedPipelineId)) {
        this.form.auto_convert_deal_pipeline_id = this.dealPipelineOptions[0]?.id || null;
      }
      const selectedStageId = parsePositiveId(this.form.auto_convert_deal_stage_id);
      if (!this.autoConvertDealStageOptions.length) return;
      if (!selectedStageId || !this.autoConvertDealStageOptions.some((item) => item.id === selectedStageId)) {
        this.form.auto_convert_deal_stage_id = this.autoConvertDealStageOptions[0]?.id || null;
      }
    },
    onAutoConvertPipelineChanged(value) {
      this.form.auto_convert_deal_pipeline_id = parsePositiveId(value);
      this.ensureAutoConvertSelection();
      this.markDirty();
    },
    onAutoMissingTaskDelayInput(value) {
      this.form.auto_create_task_if_missing_delay_minutes = normalizeAutoMissingTaskDelayMinutes(
        value,
        AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES
      );
      this.markDirty();
    },
    handleStageNameInput(index, value) {
      const stage = this.localStages[index];
      if (!stage) return;
      stage.name = normalizeNcrmLeadString(value).slice(0, 120);
      if (!stage._manual_stage_key) {
        stage.stage_key = normalizeNcrmLeadStageKey(stage.name);
        stage.stage_type = inferNcrmLeadStageTypeFromKey(stage.stage_key);
      }
      this.markDirty();
    },
    handleStageKeyInput(index) {
      const stage = this.localStages[index];
      if (!stage) return;
      stage.stage_key = normalizeNcrmLeadStageKey(stage.stage_key);
      stage._manual_stage_key = true;
      if (!normalizeNcrmLeadString(stage.stage_type)) {
        stage.stage_type = inferNcrmLeadStageTypeFromKey(stage.stage_key);
      }
      this.markDirty();
    },
    validateName() {
      const name = normalizeNcrmLeadString(this.form.name).slice(0, 190);
      if (!name) {
        this.nameErrors = [this.$t("ncrm.leads.wizard.validation.boardNameRequired")];
        return null;
      }
      this.nameErrors = [];
      return name;
    },
    validateStages() {
      const errors = [];
      const rows = [];
      const seenKeys = new Set();

      this.localStages.forEach((stage, index) => {
        const name = normalizeNcrmLeadString(stage.name).slice(0, 120);
        const stageKey = normalizeNcrmLeadStageKey(stage.stage_key || name);
        const stageType = normalizeNcrmLeadStageType(stage.stage_type, inferNcrmLeadStageTypeFromKey(stageKey));

        if (!name) {
          errors.push(this.$t("ncrm.leads.wizard.validation.stageNameRequired", { index: index + 1 }));
          return;
        }
        if (!stageKey) {
          errors.push(this.$t("ncrm.leads.wizard.validation.stageKeyInvalid", { index: index + 1 }));
          return;
        }
        if (seenKeys.has(stageKey)) {
          errors.push(this.$t("ncrm.leads.wizard.validation.stageKeyDuplicate", { key: stageKey }));
          return;
        }
        if (!stageType) {
          errors.push(this.$t("ncrm.leads.wizard.validation.stageTypeInvalid", { index: index + 1 }));
          return;
        }

        seenKeys.add(stageKey);
        rows.push({
          id: stage.id,
          name,
          stage_key: stageKey,
          stage_type: stageType,
          is_default: Boolean(stage.is_default),
          sort_order: rows.length + 1,
        });
      });

      if (!rows.length) {
        errors.push(this.$t("ncrm.leads.wizard.validation.stageMissing"));
      }

      if (rows.length) {
        const firstDefaultIndex = rows.findIndex((stage) => stage.is_default);
        if (firstDefaultIndex === -1) {
          rows[0].is_default = true;
        } else {
          rows.forEach((stage, index) => {
            stage.is_default = index === firstDefaultIndex;
          });
        }
      }

      this.stageErrors = errors;
      return errors.length ? null : rows;
    },
    handleSave() {
      const name = this.validateName();
      const stages = this.validateStages();
      if (!name || !stages) return;

      const payload = {
        name,
        entity_scope: normalizeNcrmLeadEntityScope(this.form.entity_scope, "both") || "both",
        stages,
      };
      if (this.showAutoConvertSettings) {
        payload.auto_convert_on_qualified = this.form.auto_convert_on_qualified !== false;
        payload.auto_convert_deal_pipeline_id = parsePositiveId(this.form.auto_convert_deal_pipeline_id) || null;
        payload.auto_convert_deal_stage_id = parsePositiveId(this.form.auto_convert_deal_stage_id) || null;
      }
      payload.auto_create_task_if_missing_after_change = this.form.auto_create_task_if_missing_after_change === true;
      payload.auto_create_task_if_missing_delay_minutes = normalizeAutoMissingTaskDelayMinutes(
        this.form.auto_create_task_if_missing_delay_minutes,
        AUTO_MISSING_TASK_DELAY_DEFAULT_MINUTES
      );
      this.$emit("submit", payload);
    },
  },
};
</script>

<style scoped>
:deep(.ncrm-lead-wizard-select-menu) {
  z-index: 2710 !important;
}

.ncrm-lead-wizard-stages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-lead-wizard-stage-row {
  display: grid;
  grid-template-columns: 36px minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(150px, 0.8fr) repeat(4, 36px);
  gap: 8px;
  align-items: center;
}

.ncrm-lead-wizard-stage-row__index {
  text-align: center;
}

@media (max-width: 1100px) {
  .ncrm-lead-wizard-stage-row {
    grid-template-columns: 1fr;
    border: 1px solid var(--ncrm-color-border-default);
    border-radius: var(--app-radius-base);
    padding: 10px;
  }

  .ncrm-lead-wizard-stage-row__index {
    text-align: left;
  }
}
</style>
